import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtPrice, resolveImage } from "@/lib/api";
import { compare, useLocalList } from "@/lib/storage";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Scales, X } from "@phosphor-icons/react";

const ROWS = [
  { k: "price", label: "Price", fmt: (v, p) => fmtPrice(v, p.currency) },
  { k: "listing_type", label: "Listing type", cap: true },
  { k: "property_type", label: "Property type", cap: true },
  { k: "bedrooms", label: "Bedrooms" },
  { k: "bathrooms", label: "Bathrooms" },
  { k: "area_sqft", label: "Area (sqft)", fmt: (v) => v ? Number(v).toLocaleString() : "—" },
  { k: "community", label: "Community" },
  { k: "sub_community", label: "Sub-community" },
  { k: "developer", label: "Developer" },
  { k: "furnished", label: "Furnished", cap: true },
  { k: "view", label: "View" },
  { k: "completion_date", label: "Handover" },
  { k: "amenities", label: "Amenities", fmt: (v) => (v || []).slice(0, 4).join(", ") || "—" },
];

export default function Compare() {
  const ids = useLocalList(compare);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!ids.length) { setItems([]); return; }
    Promise.all(ids.map((id) => api.get(`/listings/${id}`).then((r) => r.data).catch(() => null)))
      .then((r) => setItems(r.filter(Boolean)));
  }, [ids.join(",")]);

  return (
    <div data-testid="compare-page">
      <SEO title="Compare properties" description="Side-by-side comparison of your shortlisted Dubai properties." path="/compare" noIndex />
      <section className="pt-6 pb-6 border-b border-neutral-200">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Compare", path: "/compare" }]} />
          <div className="overline text-[#001588] mb-4">Compare</div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-tight">Side-by-side.</h1>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 overflow-x-auto">
          {items.length === 0 ? (
            <div className="border border-neutral-200 p-16 text-center text-neutral-500" data-testid="compare-empty">
              <Scales size={28} className="mx-auto mb-4 text-neutral-300" />
              Add up to 4 properties from the listings pages.
              <div className="mt-4"><Link to="/properties" className="text-[#001588] hover:underline">Browse mandates →</Link></div>
            </div>
          ) : (
            <table className="w-full text-sm min-w-[720px]" data-testid="compare-table">
              <thead>
                <tr>
                  <th className="text-left overline text-neutral-500 pb-4 w-[180px]">Attribute</th>
                  {items.map((p) => (
                    <th key={p.id} className="text-left pb-4 pr-6">
                      <Link to={`/properties/${p.id}`}>
                        {p.images?.[0] && <img src={resolveImage(p.images[0])} alt={p.title || "Compared property"} className="w-full h-32 object-cover mb-2" />}
                        <span className="font-serif text-lg text-neutral-900 block leading-tight hover:text-[#001588]">{p.title}</span>
                      </Link>
                      <button onClick={() => compare.remove(p.id)} className="text-neutral-500 hover:text-red-600 text-xs flex items-center gap-1 mt-2" data-testid={`compare-page-remove-${p.id}`}>
                        <X size={10} /> Remove
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.k} className="border-t border-neutral-200">
                    <td className="py-3 text-neutral-500 text-xs uppercase tracking-wider">{row.label}</td>
                    {items.map((p) => {
                      let v = p[row.k];
                      if (row.fmt) v = row.fmt(v, p);
                      if (row.cap && typeof v === "string") v = v.charAt(0).toUpperCase() + v.slice(1);
                      return <td key={p.id} className="py-3 pr-6 text-neutral-800">{v ?? "—"}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
